import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Loader2, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SystemStatusPanel } from "@/components/SystemStatusPanel";

const BG_URL =
    "https://static.prod-images.emergentagent.com/jobs/7a530cc1-18c9-413b-b839-2c47a7b3bd94/images/6ab7ce9c63d59f460b8790c0433188cbd28e2d68ed47c698ac4f2e1639490348.png";

const STEP_CREDENTIALS = "credentials";
const STEP_OTP = "otp";

/** 6-digit code input — single hidden text input drives a 6-cell visual.
 *  The cells are `pointer-events-none` so clicks + keyboard focus fall
 *  through to the underlying `<input>` (which preserves iOS / Android
 *  one-time-code autofill and copy-paste). */
function OtpInput({ value, onChange, disabled, error, inputRef }) {
    const cells = useMemo(() => Array.from({ length: 6 }, (_, i) => value[i] || ""), [value]);
    const handleChange = (e) => {
        const next = e.target.value.replace(/\D/g, "").slice(0, 6);
        onChange(next);
    };
    const handlePaste = (e) => {
        const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
        if (text) {
            e.preventDefault();
            onChange(text);
        }
    };
    const focus = () => inputRef.current?.focus();
    return (
        <div className="relative cursor-text" onClick={focus}>
            <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={value}
                disabled={disabled}
                onChange={handleChange}
                onPaste={handlePaste}
                className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-text"
                data-testid="mfa-code-input"
                aria-label="6-digit verification code"
            />
            <div className="grid grid-cols-6 gap-2 pointer-events-none">
                {cells.map((ch, i) => (
                    <div
                        key={i}
                        className={`h-12 rounded-md border flex items-center justify-center text-lg font-mono tabular-nums transition-all
              ${error
                                ? "border-red-400/50 bg-red-400/5"
                                : ch
                                    ? "border-[#a17bff]/60 bg-[#7C3AED]/10 text-white"
                                    : "border-white/10 bg-[#050505] text-white/40"
                            }
              ${value.length === i ? "ring-2 ring-white/20" : ""}`}
                    >
                        {ch || (value.length === i ? <span className="text-white/30 animate-pulse">|</span> : "")}
                    </div>
                ))}
            </div>
        </div>
    );
}

function useCountdown(targetIso) {
    const [remaining, setRemaining] = useState(0);
    useEffect(() => {
        if (!targetIso) {
            setRemaining(0);
            return;
        }
        const target = new Date(targetIso).getTime();
        const tick = () => setRemaining(Math.max(0, Math.floor((target - Date.now()) / 1000)));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [targetIso]);
    return remaining;
}

export default function LoginPage() {
    const { login, verifyMfa, resendMfa, user } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState(STEP_CREDENTIALS);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [loading, setLoading] = useState(false);

    // MFA state
    const [mfa, setMfa] = useState(null); // {challenge_id, delivery_email_masked, expires_at}
    const [code, setCode] = useState("");
    const [codeError, setCodeError] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const otpInputRef = useRef(null);

    const remaining = useCountdown(mfa?.expires_at);

    useEffect(() => {
        if (user && user !== false) navigate("/", { replace: true });
    }, [user, navigate]);

    useEffect(() => {
        if (step === STEP_OTP) {
            // small delay so the input mounts before focus
            setTimeout(() => otpInputRef.current?.focus(), 50);
        }
    }, [step]);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [resendCooldown]);

    const onSubmitCredentials = async (e) => {
        e.preventDefault();
        setError("");
        setInfo("");
        setLoading(true);
        const res = await login(email.trim(), password);
        setLoading(false);
        if (!res.ok) {
            setError(res.error || "Login failed");
            return;
        }
        if (res.mfa) {
            setMfa(res.mfa);
            setCode("");
            setStep(STEP_OTP);
            setInfo(`We sent a 6-digit code to ${res.mfa.delivery_email_masked}.`);
            setResendCooldown(30);
        } else {
            navigate("/", { replace: true });
        }
    };

    const onSubmitOtp = async (e) => {
        e?.preventDefault?.();
        if (code.length !== 6 || !mfa) return;
        setError("");
        setCodeError(false);
        setLoading(true);
        const res = await verifyMfa(mfa.challenge_id, code);
        setLoading(false);
        if (res.ok) {
            navigate("/", { replace: true });
            return;
        }
        if (res.expired) {
            // Challenge invalid/expired/used — drop back to step 1
            setError(res.error || "Verification expired — please sign in again");
            setStep(STEP_CREDENTIALS);
            setMfa(null);
            setCode("");
            return;
        }
        setCodeError(true);
        setError(res.error || "Incorrect code");
        setCode("");
    };

    // auto-submit when 6 digits are typed
    useEffect(() => {
        if (step === STEP_OTP && code.length === 6 && !loading) {
            onSubmitOtp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code, step]);

    const onResend = async () => {
        if (!mfa || resendCooldown > 0) return;
        setError("");
        setInfo("");
        setLoading(true);
        const res = await resendMfa(mfa.challenge_id);
        setLoading(false);
        if (res.ok) {
            setMfa(res.mfa);
            setCode("");
            setCodeError(false);
            setInfo(`A new code was sent to ${res.mfa.delivery_email_masked}.`);
            setResendCooldown(30);
        } else if (res.expired) {
            setError(res.error || "Verification expired — please sign in again");
            setStep(STEP_CREDENTIALS);
            setMfa(null);
        } else {
            setError(res.error || "Could not resend code");
        }
    };

    const onBack = () => {
        setStep(STEP_CREDENTIALS);
        setError("");
        setInfo("");
        setMfa(null);
        setCode("");
    };

    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;

    return (
        <div
            className="min-h-screen bg-[#050505] relative"
            style={{
                backgroundImage: `url(${BG_URL})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: "fixed",
            }}
        >
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
            <div className="relative min-h-screen grid lg:grid-cols-2">
                {/* LEFT — public live status (no auth required) */}
                <aside
                    className="flex items-center justify-center px-6 lg:px-12 py-10 lg:py-0 border-b lg:border-b-0 lg:border-r border-white/5"
                    data-testid="login-status-pane"
                >
                    <SystemStatusPanel />
                </aside>

                {/* RIGHT — credentials / OTP flow */}
                <section
                    className="flex items-center justify-center px-6 py-10 lg:py-0"
                    data-testid="login-form-pane"
                >
                    <div className="w-full max-w-[420px] mx-auto animate-fade-in">
                        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-7">
                            {step === STEP_CREDENTIALS ? (
                                <>
                                    <div className="flex items-center gap-2 mb-1 text-white/50">
                                        <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        <span className="label-xs">Admin Access</span>
                                    </div>
                                    <h1 className="text-2xl font-display tracking-tight mb-6">
                                        Sign in to AIService
                                    </h1>
                                    <form onSubmit={onSubmitCredentials} className="space-y-4" autoComplete="off">
                                        <div>
                                            <label className="label-xs block mb-2">Email</label>
                                            <input
                                                type="email"
                                                required
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10 transition-all"
                                                data-testid="login-email-input"
                                                placeholder="you@example.com"
                                                autoComplete="off"
                                                spellCheck={false}
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="label-xs block">Password</label>
                                                <Link to="/forgot-password" className="text-[11px] text-[#a17bff] hover:text-[#c7a6ff]" data-testid="forgot-password-link">
                                                    Forgot password?
                                                </Link>
                                            </div>
                                            <input
                                                type="password"
                                                required
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10 transition-all"
                                                data-testid="login-password-input"
                                                autoComplete="new-password"
                                            />
                                        </div>
                                        {error && (
                                            <div
                                                className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md px-3 py-2"
                                                data-testid="login-error"
                                            >
                                                {error}
                                            </div>
                                        )}
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            data-testid="login-submit-button"
                                            className="w-full bg-white text-black font-medium text-sm py-2.5 rounded-md hover:bg-white/90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
                                        >
                                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                            {loading ? "Signing in…" : "Sign in"}
                                        </button>
                                    </form>
                                    <div className="mt-6 pt-5 border-t border-white/5 text-[11px] text-white/40 font-mono">
                                        Sign in with your administrator credentials. After your password is verified
                                        you'll receive a one-time code by email to finish signing in.
                                    </div>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={onBack}
                                        data-testid="mfa-back-button"
                                        className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-xs mb-4 transition-colors"
                                    >
                                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        Back
                                    </button>
                                    <div className="flex items-center gap-2 mb-1 text-white/50">
                                        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        <span className="label-xs">Two-Factor Verification</span>
                                    </div>
                                    <h1 className="text-2xl font-display tracking-tight mb-3">
                                        Enter your code
                                    </h1>
                                    <p
                                        className="text-xs text-white/55 leading-relaxed mb-6 flex items-start gap-2"
                                        data-testid="mfa-delivery-info"
                                    >
                                        <Mail className="w-3.5 h-3.5 mt-0.5 text-[#a17bff]" strokeWidth={1.5} />
                                        <span>
                                            We sent a 6-digit code to{" "}
                                            <span className="font-mono text-white/85">
                                                {mfa?.delivery_email_masked}
                                            </span>
                                            . The code expires in{" "}
                                            <span className="font-mono text-white" data-testid="mfa-countdown">
                                                {minutes}:{String(seconds).padStart(2, "0")}
                                            </span>
                                            .
                                        </span>
                                    </p>
                                    <form onSubmit={onSubmitOtp} className="space-y-4">
                                        <OtpInput
                                            value={code}
                                            onChange={(v) => {
                                                setCode(v);
                                                setCodeError(false);
                                                setError("");
                                            }}
                                            disabled={loading}
                                            error={codeError}
                                            inputRef={otpInputRef}
                                        />
                                        {info && !error && (
                                            <div
                                                className="text-xs text-[#a17bff] border border-[#a17bff]/25 bg-[#7C3AED]/5 rounded-md px-3 py-2"
                                                data-testid="mfa-info"
                                            >
                                                {info}
                                            </div>
                                        )}
                                        {error && (
                                            <div
                                                className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 rounded-md px-3 py-2"
                                                data-testid="mfa-error"
                                            >
                                                {error}
                                            </div>
                                        )}
                                        <button
                                            type="submit"
                                            disabled={loading || code.length !== 6 || remaining === 0}
                                            data-testid="mfa-submit-button"
                                            className="w-full bg-white text-black font-medium text-sm py-2.5 rounded-md hover:bg-white/90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
                                        >
                                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                            {loading ? "Verifying…" : remaining === 0 ? "Code expired" : "Verify & sign in"}
                                        </button>
                                    </form>
                                    <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between text-[11px] text-white/40 font-mono">
                                        <span>Didn't receive it?</span>
                                        <button
                                            type="button"
                                            onClick={onResend}
                                            disabled={loading || resendCooldown > 0}
                                            data-testid="mfa-resend-button"
                                            className="text-[#a17bff] hover:text-white disabled:text-white/25 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
